import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TicketVenta from '../TicketVenta';

// Componentes modulares
import VentaCard from './components/VentaCard';
import DateFilters from './components/DateFilters';

// Nuevo: Filtro por ID de venta
import { useState as useLocalState } from 'react';
import SkeletonLoader from './components/SkeletonLoader';
import ErrorNotification from './components/ErrorNotification';
import VentaDetails from './components/VentaDetails';
import ResultsIndicator from './components/ResultsIndicator';
import InfiniteScrollTrigger from './components/InfiniteScrollTrigger';

// Hooks personalizados
import { useVentas } from './hooks/useVentas';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

const ShowVentas = () => {
  const [selectedVenta, setSelectedVenta] = useState(null);
  const [ventaParaImprimir, setVentaParaImprimir] = useState(null);
  const [currentFilters, setCurrentFilters] = useState({ fechaInicio: '', fechaFin: '' });
  // Estado local para búsqueda por ID
  const [searchVentaId, setSearchVentaId] = useLocalState('');
  
  const navigate = useNavigate();
  const ticketRef = useRef();
  const filtersRef = useRef(currentFilters); // Ref para filtros estables

  // Actualizar ref cuando cambien los filtros
  useEffect(() => {
    filtersRef.current = currentFilters;
  }, [currentFilters]);

  // Hook personalizado para manejar las ventas
  const {
    ventas,
    loading,
    loadingMore,
    error,
    currentPage,
    hasMoreVentas,
    totalVentas,
    isSearching,
    searchByDates,
    resetFilters,
    clearError,
    loadMoreVentas,
    searchById
  } = useVentas();

  // Hook para infinite scroll con filtros estables
  const loadMoreWithFilters = useCallback(() => {
    const filters = filtersRef.current;
    console.log('🎯 loadMoreWithFilters:', filters);
    loadMoreVentas(filters.fechaInicio, filters.fechaFin);
  }, [loadMoreVentas]); // Solo depender de loadMoreVentas

  // Hook para infinite scroll
  const observerRef = useInfiniteScroll(
    hasMoreVentas,
    loadingMore,
    loading,
    isSearching,
    loadMoreWithFilters
  );

  console.log('🏠 Component state:', {
    hasMore: hasMoreVentas,
    loading,
    loadingMore,
    isSearching,
    filters: currentFilters,
    count: ventas.length
  });

  // Handlers
  const handleVerDetalles = useCallback((venta) => {
    setSelectedVenta(venta);
  }, []);

  const handleDevolucion = useCallback((venta) => {
    navigate(`/devolucion/${venta.id}`);
  }, [navigate]);


  const handleSearch = useCallback((fechaInicio, fechaFin) => {
    setCurrentFilters({ fechaInicio, fechaFin });
    searchByDates(fechaInicio, fechaFin);
  }, [searchByDates]);

  // Nuevo: búsqueda por ID
  const handleSearchById = (e) => {
    e.preventDefault();
    if (searchVentaId.trim() !== '') {
      searchById(searchVentaId.trim());
    }
  };

  // Limpiar filtro de ID y mostrar todas las ventas
  const handleClearVentaId = () => {
    setSearchVentaId('');
    handleReset();
  };

  const handleReset = useCallback(() => {
    setCurrentFilters({ fechaInicio: '', fechaFin: '' });
    resetFilters();
  }, [resetFilters]);

  // Memoizar la conversión de datos para el ticket
  const convertVentaToTicketData = useCallback((venta) => {
  // Backend: venta.total ya viene con descuento aplicado (total final).
  const totalFinal = parseFloat(venta.total) || 0;
  const descuento = parseFloat(venta.descuento) || 0;
  const totalAntes = totalFinal + descuento; // monto antes de aplicar descuento

    return {
      cart: venta.detalles_venta ? venta.detalles_venta.map(detalle => ({
        id: detalle.id,
        nombre_producto: detalle.producto?.nombre_producto || 'Producto',
        quantity: parseFloat(detalle.cantidad) || 1,
        precios: [{
          precio: parseFloat(detalle.precio) || 0
        }]
      })) : [],
  // Seguimos pasando 'total' como monto *antes* si alguna parte del UI lo espera,
  // pero TicketVenta ahora recibe 'total' y 'totalWithDiscount' con claridad.
  total: totalAntes.toFixed(2),
  discount: descuento.toFixed(2),
  totalWithDiscount: totalFinal.toFixed(2),
      paymentMethod: "Efectivo",
      selectedWholesalePrice: "precio",
      tipo: "normal",
      cliente: venta.cliente || null,
      fecha: new Date(venta.createdAt),
      negocio: "Zafpan"
    };
  }, []);

  const handleImprimirTicket = useCallback((venta) => {
    setVentaParaImprimir(venta);
    
    setTimeout(() => {
      const ticketContent = ticketRef.current;
      if (ticketContent) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Ticket de Venta #${venta.id}</title>
              <style>
                @media print {
                  @page { 
                    size: 8cm auto; 
                    margin: 0; 
                  }
                  body { 
                    margin: 0; 
                    padding: 0; 
                  }
                }
                body {
                  font-family: monospace;
                  width: 8cm;
                  margin: 0;
                  padding: 10px;
                  background: white;
                  color: black;
                  font-size: 13px;
                  line-height: 1.2;
                }
                hr {
                  border: none;
                  border-top: 1px dashed #000;
                  margin: 8px 0;
                }
              </style>
            </head>
            <body>
              ${ticketContent.innerHTML}
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        printWindow.onload = () => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            setVentaParaImprimir(null);
          };
        };
      }
    }, 200);
  }, []);

  // Componente de estado vacío
  const EmptyState = () => (
    <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
      <div className="bg-gradient-to-r from-gray-100 to-gray-200 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
        <span className="text-gray-400 text-4xl">📊</span>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-2">No se encontraron ventas</h3>
      <p className="text-gray-600 mb-6">
        No hay ventas que coincidan con los criterios de búsqueda seleccionados.
      </p>
      <button
        onClick={handleReset}
        disabled={loading || isSearching}
        className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-indigo-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none"
      >
        🔄 Mostrar todas las ventas
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header principal */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-3 rounded-xl">
              <span className="text-white text-2xl">📊</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Historial de Ventas
              </h1>
              <p className="text-gray-600">Consulta y gestiona el historial completo de ventas</p>
            </div>
          </div>

          {/* Filtros de fecha */}
          <DateFilters
            onSearch={handleSearch}
            onReset={handleReset}
            isSearching={isSearching}
            loading={loading}
            error={error}
            searchVentaId={searchVentaId}
            setSearchVentaId={setSearchVentaId}
            onSearchById={handleSearchById}
            onClearVentaId={handleClearVentaId}
          />

          {/* Filtro por número de venta (ID) ahora se muestra en el área de consejos de DateFilters */}
        </div>

        {/* Notificación de error */}
        <ErrorNotification error={error} onClose={clearError} />

        {/* Contenido principal */}
        {loading ? (
          <SkeletonLoader />
        ) : ventas.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Indicador de resultados */}
            <ResultsIndicator
              ventasCount={ventas.length}
              totalVentas={totalVentas}
              currentPage={currentPage}
              hasMore={hasMoreVentas}
              fechaInicio={currentFilters.fechaInicio}
              fechaFin={currentFilters.fechaFin}
            />

            {/* Grid de ventas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ventas.map((venta, index) => (
                <VentaCard
                  key={venta.id}
                  venta={venta}
                  index={index}
                  onVerDetalles={handleVerDetalles}
                  onImprimirTicket={handleImprimirTicket}
                  onDevolucion={handleDevolucion}
                />
              ))}
            </div>

            {/* Infinite scroll trigger */}
            <InfiniteScrollTrigger
              hasMore={hasMoreVentas}
              loadingMore={loadingMore}
              totalVentas={totalVentas}
              observerRef={observerRef}
            />
          </>
        )}

        {/* Modal de detalles de venta */}
        <VentaDetails
          venta={selectedVenta}
          onClose={() => setSelectedVenta(null)}
          onImprimirTicket={handleImprimirTicket}
          onDevolucion={handleDevolucion}
        />

        {/* Componente TicketVenta para impresión (oculto) */}
        {ventaParaImprimir && (
          <div style={{ display: 'none' }}>
            <TicketVenta
              ref={ticketRef}
              {...convertVentaToTicketData(ventaParaImprimir)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowVentas;
